<script lang="ts">
	import type { Customer, PaymentMethod, SalesOrderTerm, State, TaxItem } from 'utility/customer-helper'
	import type { CrudStore } from '@isoftdata/svelte-store-crud'
	import type { i18n } from 'types/common'

	import { getEventChecked, getEventValue } from '@isoftdata/browser-event'
	import { customerBalanceStatusMap, formatCustomerCurrency } from 'utility/customer-helper'
	import { priceTypeOptions } from 'utility/customer-helper'
	import { hasPermission } from 'utility/permission'
	import { getContext, tick } from 'svelte'
	import { v4 as uuid } from '@lukeed/uuid'

	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import CurrencyInput from '@isoftdata/svelte-currency-input'
	import Input from '@isoftdata/svelte-input'
	import Label from '@isoftdata/svelte-label'
	import Select from '@isoftdata/svelte-select'
	import Table, { type Column, Td } from '@isoftdata/svelte-table'

	interface Props {
		customer: Customer
		states?: Array<State>
		paymentMethods?: Array<PaymentMethod>
		salesOrderTerms?: Array<SalesOrderTerm>
		taxItems?: Array<TaxItem>
		customerChanged: boolean
		viewOnlyMode?: boolean
	}

	let {
		customer = $bindable(),
		states = [],
		paymentMethods = [],
		salesOrderTerms = [],
		taxItems = [],
		customerChanged = $bindable(),
		viewOnlyMode = false,
	}: Props = $props()

	let useCustomLaborRate: boolean = $state(customer.laborRateType !== 'DEFAULT')
	let showExpired: boolean = $state(false)
	let selectedStateFilter: string | null = $state(null)

	const { t: translate } = getContext<i18n>('i18next')

	const paymentMethodCrudStore =
		getContext<CrudStore<Customer['allowedPaymentMethods'][number], 'id'>>('paymentMethodCrudStore')
	const taxExemptionCrudStore =
		getContext<CrudStore<Customer['taxExemptions'][number], 'uuid'>>('taxExemptionCrudStore')

	const columns: Array<Column> = [
		{ property: 'state', name: translate('common:state', 'State') },
		{ property: 'number', name: translate('common:taxId', 'Tax ID'), align: 'right' },
		{ property: 'expirationDate', name: translate('common:expiration', 'Expiration'), align: 'right' },
		{ property: 'notes', name: translate('common:notes', 'Notes') },
		{ property: 'deleteAction', name: '', align: 'center', width: '1rem' },
	]
	let initialCustomerAllowedPaymentMethodIds: Array<number> = customer.allowedPaymentMethods.map(
		paymentMethod => paymentMethod.id,
	)

	function selectAndUnselectAllPaymentMethods() {
		paymentMethodCrudStore.clear()
		if (customer.allowedPaymentMethods.length === paymentMethods.length) {
			customer.allowedPaymentMethods.forEach(allowedPaymentMethod => {
				if (initialCustomerAllowedPaymentMethodIds.includes(allowedPaymentMethod.id)) {
					paymentMethodCrudStore.delete(allowedPaymentMethod)
				}
			})
			customer.allowedPaymentMethods = []
			customer.defaultPaymentMethod = null
		} else {
			customer.allowedPaymentMethods = paymentMethods
			customer.allowedPaymentMethods.forEach(allowedPaymentMethod => {
				if (!initialCustomerAllowedPaymentMethodIds.includes(allowedPaymentMethod.id)) {
					paymentMethodCrudStore.create(allowedPaymentMethod)
				}
			})
		}
		customerChanged = true
	}

	function toggleSinglePaymentMethod(paymentMethodId: number) {
		const selectedPaymentMethod = paymentMethods.find(paymentMethod => paymentMethod.id === paymentMethodId)
		if (!selectedPaymentMethod) {
			return
		} else if (customerAllowedPaymentMethodIds.includes(paymentMethodId)) {
			// Remove the payment method from the customer's allowed payment methods
			customer.allowedPaymentMethods = customer.allowedPaymentMethods.filter(
				allowedPaymentMethod => allowedPaymentMethod.id !== paymentMethodId,
			)
			if (initialCustomerAllowedPaymentMethodIds.includes(paymentMethodId)) {
				paymentMethodCrudStore.delete(selectedPaymentMethod)
			}

			const createdPaymentMethods = Object.values($paymentMethodCrudStore.created)
			if (createdPaymentMethods.includes(selectedPaymentMethod)) {
				paymentMethodCrudStore.clear()
				createdPaymentMethods.forEach(createdPaymentMethod => {
					if (createdPaymentMethod.id !== paymentMethodId) {
						paymentMethodCrudStore.create(createdPaymentMethod)
					}
				})
			}

			if (customer.defaultPaymentMethod?.id === paymentMethodId) {
				customer.defaultPaymentMethod = null
			}
		} else {
			// Add the payment method to the customer's allowed payment methods
			customer.allowedPaymentMethods = [...customer.allowedPaymentMethods, selectedPaymentMethod]
			if (!initialCustomerAllowedPaymentMethodIds.includes(paymentMethodId)) {
				paymentMethodCrudStore.create(selectedPaymentMethod)
			} else {
				const deletedPaymentMethod = Object.values($paymentMethodCrudStore.deleted).find(
					deletedPaymentMethod => deletedPaymentMethod.id === paymentMethodId,
				)
				if (deletedPaymentMethod) {
					paymentMethodCrudStore.unDelete(deletedPaymentMethod)
				}
			}
		}
		customerChanged = true
	}

	async function addTaxExemption() {
		const newTaxExemption = {
			id: null,
			uuid: uuid(),
			state: '',
			number: '',
			expirationDate: null,
			notes: '',
		}
		customer.taxExemptions.push(newTaxExemption)
		customer.taxExemptions = customer.taxExemptions
		taxExemptionCrudStore.create(newTaxExemption)
		await tick()
		document.getElementById(`tax-exemption-${customer.taxExemptions.length - 1}`)?.focus()
	}

	function updateTaxExemption(event: Event, index: number, key: string) {
		const value = getEventValue(event)
		customer.taxExemptions[index][key] =
			customer.taxExemptions[index][key] === 'expirationDate' ? new Date(value) : value
		taxExemptionCrudStore.update(customer.taxExemptions[index])
		customerChanged = true
	}

	const customerAllowedPaymentMethodIds = $derived(
		customer.allowedPaymentMethods.map(paymentMethod => paymentMethod.id),
	)
	const customerTaxExemptions = $derived(
		selectedStateFilter
			? customer.taxExemptions.filter(taxExemption => taxExemption.state === selectedStateFilter)
			: customer.taxExemptions,
	)
</script>

<div class="row">
	<div class="col-12">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">{translate('customer.balanceInformation', 'Balance Information')}</h5>
			</div>
			<div class="card-body">
				<div class="row">
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end"
							label={translate('common:dateRangeLast30Days', 'Last 30 Days')}
							value={formatCustomerCurrency(customer.aging.zeroToThirtyDays)}
						/>
					</div>
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end"
							label={translate('customer.31To60Days', '31 to 60 Days')}
							value={formatCustomerCurrency(customer.aging.thirtyOneToSixtyDays)}
						/>
					</div>
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end"
							label={translate('customer.61To90Days', '61 to 90 Days')}
							value={formatCustomerCurrency(customer.aging.sixtyOneToNinetyDays)}
						/>
					</div>
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end"
							label={translate('customer.91To120Days', '91 to 120 Days')}
							value={formatCustomerCurrency(customer.aging.ninetyOneToOneHundredTwentyDays)}
						/>
					</div>
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end"
							label={translate('customer.over120Days', 'Over 120 Days')}
							value={formatCustomerCurrency(customer.aging.overOneHundredTwentyDays)}
						/>
					</div>
					<div class="col-4 col-md-2">
						<Input
							readonly
							class="text-end {customerBalanceStatusMap[customer.balanceStatus].textColor}"
							label={translate('customer.totalDue', 'Total Due')}
							value={formatCustomerCurrency(customer.balance)}
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-6 mt-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('customer.documentSettingsDefaults', 'Document Settings & Defaults')}</h5>
			</div>
			<div class="card-body">
				<div class="row">
					<div class="col-12 col-md-6">
						<!-- TODO: add some description for these checkboxes, should be able to get it from the desktop code -->
						<Checkbox
							label={translate('customer.requirePoOnSalesOrders', 'Require PO# On Sales Orders')}
							disabled={viewOnlyMode}
							bind:checked={customer.requirePurchaseOrderNumber}
							onchange={() => (customerChanged = true)}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label={translate('customer.acceptsPartialShipments', 'Accepts Partial Shipments')}
							disabled={viewOnlyMode}
							bind:checked={customer.acceptsPartialShipments}
							onchange={() => (customerChanged = true)}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label={translate(
								'customer.requireFullPaymentOnInvoiceFinalization',
								'Require Full Payment On Invoice Finalization',
							)}
							disabled={viewOnlyMode}
							bind:checked={customer.cashOnly}
							onchange={() => (customerChanged = true)}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Checkbox
							label={translate('customer.acceptsBackOrders', 'Accepts Back Orders')}
							disabled={viewOnlyMode}
							bind:checked={customer.acceptsBackOrders}
							onchange={() => (customerChanged = true)}
						/>
					</div>

					<hr class="w-100 mb-1" />
					<div class="col-12">
						<CurrencyInput
							labelParentClass="w-50"
							label={translate('customer.accountLimit', 'Account Limit')}
							disabled={viewOnlyMode || !hasPermission('CUSTOMERS_CAN_EDIT_CREDIT_LIMIT', customer.store?.id)}
							bind:value={customer.accountLimit}
							change={() => (customerChanged = true)}
						/>
					</div>
					<hr class="w-100 mb-1" />
					<div class="col-12 col-md-6">
						<div class="mb-3">
							<Label
								required
								parentClass="mb-0"
								label={translate('customer.defaultPricingLevel', 'Default Pricing Level')}
							/>
							<div class="d-flex align-items-center">
								<div class="input-group input-group-sm w-auto">
									<input
										required
										type="number"
										inputmode="decimal"
										class="form-control"
										disabled={viewOnlyMode}
										bind:value={customer.defaultPricePercentage}
										onchange={() => {
											if (!customer.defaultPricePercentage) {
												customer.defaultPricePercentage = '0'
											}
											customerChanged = true
										}}
									/>
									<span class="input-group-text">%</span>
								</div>
								<span class="ms-2 me-2">of</span>
								<Select
									required
									showLabel={false}
									showEmptyOption={false}
									labelParentClass="mb-0 w-100"
									disabled={viewOnlyMode}
									bind:value={customer.defaultPriceType}
									onchange={() => (customerChanged = true)}
								>
									{#each priceTypeOptions as priceTypeOption}
										<option value={priceTypeOption.value}>{priceTypeOption.label}</option>
									{/each}
								</Select>
							</div>
						</div>
					</div>
					<div class="col-12 col-md-6">
						<div class="col-form-label">
							<Checkbox
								inline
								label={translate('customer.useCustomWoLaborRate', 'Use Custom WO Labor Rate')}
								disabled={viewOnlyMode}
								bind:checked={useCustomLaborRate}
								onchange={() => {
									if (!useCustomLaborRate) {
										customer.customLaborRate = '0'
										customer.laborRateType = 'DEFAULT'
									}
									customerChanged = true
								}}
							/>
						</div>
						{#if customer.laborRateType === 'PERCENT'}
							<div class="input-group input-group-sm w-auto">
								<input
									type="number"
									inputmode="decimal"
									class="form-control"
									placeholder="0"
									disabled={!useCustomLaborRate || viewOnlyMode}
									bind:value={customer.customLaborRate}
									onchange={() => (customerChanged = true)}
								/>
								<span class="input-group-text">%</span>
							</div>
						{:else}
							<CurrencyInput
								showLabel={false}
								labelParentClass="mb-0"
								required={useCustomLaborRate}
								disabled={!useCustomLaborRate || viewOnlyMode}
								bind:value={customer.customLaborRate}
								change={() => (customerChanged = true)}
							/>
						{/if}
						<Checkbox
							label={translate('customer.usePercent', 'Use Percent')}
							checked={customer.laborRateType === 'PERCENT'}
							disabled={!useCustomLaborRate || viewOnlyMode}
							onchange={event => {
								const value = getEventChecked(event)
								customer.laborRateType = value ? 'PERCENT' : 'FIXED'
							}}
						/>
					</div>

					<div class="col-12 col-md-6">
						<Select
							label={translate('customer.defaultInvoiceTerms', 'Default Invoice Terms')}
							emptyValue={null}
							options={salesOrderTerms.map(term => term.name)}
							value={customer.defaultTerm?.name ?? null}
							disabled={viewOnlyMode || !hasPermission('CUSTOMERS_CAN_EDIT_TERMS', customer.store?.id)}
							onchange={event => {
								const value = getEventValue(event)
								const selectedDefaultTerm = salesOrderTerms.find(term => term.name === value)
								if (selectedDefaultTerm) {
									customer.defaultTerm = selectedDefaultTerm
								}
								customerChanged = true
							}}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Select
							label={translate('customer.defaultInvoiceTax', 'Default Invoice Tax')}
							emptyValue={null}
							options={taxItems.map(taxItem => taxItem.name)}
							value={customer.defaultTaxItem?.name ?? null}
							disabled={viewOnlyMode}
							onchange={event => {
								const value = getEventValue(event)
								const selectedDefaultTaxItem = taxItems.find(taxItem => taxItem.name === value)
								if (selectedDefaultTaxItem) {
									customer.defaultTaxItem = selectedDefaultTaxItem
								}
								customerChanged = true
							}}
						/>
					</div>
					<!-- Will come back to this once we got a clarification on this feature -->
					<!-- <div class="col-12 col-md-6">
						<Input
							label="Default PO#"
							value={customer.blanketPurchaseOrderNumber}
						/>
					</div>
					<div class="col-12 col-md-6">
						<Input
							type="date"
							label="PO# Expiration Date"
							value={customer.blanketPurchaseOrderNumberExpiration ? customer.blanketPurchaseOrderNumberExpiration.toISOString().split('T')[0] : null}
						/>
					</div> -->
				</div>
			</div>
		</div>
	</div>
	<div class="col-12 col-md-6 mt-2">
		<div class="card h-100">
			<div class="card-header">
				<h5 class="mb-0">{translate('common:paymentMethods', 'Payment Methods')}</h5>
			</div>
			<div class="card-body">
				<Select
					labelParentClass="mb-2"
					label={translate('common:default', 'Default')}
					emptyValue={null}
					emptyText={translate('customer.noDefaultPaymentMethod', 'No Default Payment Method')}
					options={customer.allowedPaymentMethods.map(paymentMethod => paymentMethod.name)}
					value={customer.defaultPaymentMethod?.name ?? null}
					disabled={viewOnlyMode}
					onchange={event => {
						const value = getEventValue(event)
						const selectedDefaultPaymentMethod = paymentMethods.find(paymentMethod => paymentMethod.name === value)
						if (selectedDefaultPaymentMethod) {
							customer.defaultPaymentMethod = selectedDefaultPaymentMethod
						} else {
							customer.defaultPaymentMethod = null
						}
						customerChanged = true
					}}
				/>
				<Label
					parentClass="mb-0"
					label={translate('common:allowed', 'Allowed')}
				>
					<div
						class="list-group"
						style="max-height: 35vh; overflow-y: auto;"
					>
						{#each paymentMethods as paymentMethod}
							<button
								class="list-group-item list-group-item-action"
								disabled={viewOnlyMode}
								class:list-group-item-primary={customerAllowedPaymentMethodIds.includes(paymentMethod.id)}
								onclick={() => toggleSinglePaymentMethod(paymentMethod.id)}
							>
								<div class="d-flex justify-content-between align-items-center">
									<div>
										<i
											class={`fas fa-fw ${customerAllowedPaymentMethodIds.includes(paymentMethod.id) ? 'fa-check' : ''}`}
										></i>
										{paymentMethod.name}
									</div>
									{#if customer.defaultPaymentMethod?.name === paymentMethod.name}
										<span class="badge rounded-pill text-bg-success">{translate('common:default', 'Default')}</span>
									{/if}
								</div>
							</button>
						{/each}
					</div>
				</Label>
			</div>
			<div class="card-footer">
				<Button
					outline
					size="sm"
					color="primary"
					iconClass="check-double"
					disabled={viewOnlyMode}
					onclick={selectAndUnselectAllPaymentMethods}
				>
					{customer.allowedPaymentMethods.length === paymentMethods.length
						? translate('customer.disallowAll', 'Disallow All')
						: translate('customer.allowAll', 'Allow All')}
				</Button>
			</div>
		</div>
	</div>
	<div class="col-12 mt-2">
		<div class="card">
			<div class="card-header">
				<h5 class="mb-0">{translate('customer.taxExemptions', 'Tax Exemptions')}</h5>
			</div>
			<div class="card-body">
				<div class="row align-items-end mb-1">
					<div class="col-2">
						<Select
							label={translate('customer.stateFilter', 'State Filter')}
							emptyText={translate('customer.allStates', 'All States')}
							options={states.map(state => state.stateAbbreviation)}
							bind:value={selectedStateFilter}
						/>
					</div>
					<div class="col-2">
						<Checkbox
							inline
							label={translate('common:showExpired', 'Show Expired')}
							bind:checked={showExpired}
						/>
					</div>
				</div>
				<Table
					responsive
					{columns}
					parentStyle="overflow-y: auto; max-height: 45vh;"
					rows={customerTaxExemptions}
				>
					{#snippet noRows({ visibleColumnsCount })}
						<tr>
							<td
								class="text-center"
								colspan={visibleColumnsCount}
							>
								{translate('customer.noTaxExemptions', 'No Tax Exemptions')}
							</td>
						</tr>
					{/snippet}
					{#snippet children({ row })}
						{#if showExpired || row.number.toLowerCase() !== 'expired'}
							<tr>
								<Td property="state">
									<Select
										showLabel={false}
										emptyValue=""
										emptyText={translate('common:notSlashAcceptable', 'N/A')}
										options={states.map(state => state.stateAbbreviation)}
										value={row.state}
										disabled={viewOnlyMode}
										onchange={event => {
											updateTaxExemption(event, row.originalIndex, 'state')
										}}
									/>
								</Td>
								<Td property="number">
									<Input
										required
										showLabel={false}
										id="tax-exemption-{row.originalIndex}"
										value={row.number}
										disabled={viewOnlyMode}
										onchange={event => {
											updateTaxExemption(event, row.originalIndex, 'number')
										}}
									/>
								</Td>
								<Td property="expirationDate">
									<Input
										showLabel={false}
										type="date"
										value={row.expirationDate ? new Date(row.expirationDate).toISOString().split('T')[0] : null}
										disabled={viewOnlyMode}
										onchange={event => {
											updateTaxExemption(event, row.originalIndex, 'expirationDate')
										}}
									/>
								</Td>
								<Td property="notes">
									<Input
										showLabel={false}
										value={row.notes}
										disabled={viewOnlyMode}
										onchange={event => {
											updateTaxExemption(event, row.originalIndex, 'notes')
										}}
									/>
								</Td>
								<Td property="deleteAction">
									<Button
										outline
										size="sm"
										color="danger"
										iconClass="trash"
										disabled={viewOnlyMode}
										onclick={() => {
											customer.taxExemptions = customer.taxExemptions.filter(
												taxExemption => taxExemption.uuid !== row.uuid,
											)
											taxExemptionCrudStore.delete(row)
											customerChanged = true
										}}
									/>
								</Td>
							</tr>
						{/if}
					{/snippet}
				</Table>
			</div>
			<div class="card-footer">
				<Button
					outline
					size="sm"
					color="success"
					iconClass="plus"
					disabled={viewOnlyMode}
					onclick={() => addTaxExemption()}
				>
					{translate('customer.newTaxExemption', 'New Tax Exemption')}
				</Button>
			</div>
		</div>
	</div>
</div>
